import { FormulaEquation } from '../../types';

export const THERMO_ELECTRO_EQUATIONS: FormulaEquation[] = [
  // --- TERMOLOGIA ---
  {
    id: 'calorimetria_sensivel',
    name: 'Equação Fundamental da Calorimetria',
    subject: 'FISICA',
    latex: 'Q = m \\cdot c \\cdot \\Delta T',
    variables: ['Q_cal', 'm', 'c_esp', 'deltaT'],
    description: 'Calor trocado por um corpo com variação de temperatura sem mudança de fase física.',
    rearrangedLatex: {
      Q_cal: 'Q = m \\cdot c \\cdot \\Delta T',
      m: 'm = \\frac{Q}{c \\cdot \\Delta T}',
      c_esp: 'c = \\frac{Q}{m \\cdot \\Delta T}',
      deltaT: '\\Delta T = \\frac{Q}{m \\cdot c}',
    },
    descriptorCode: 'FIS_D22',
  },
  {
    id: 'capacidade_termica',
    name: 'Capacidade Térmica',
    subject: 'FISICA',
    latex: 'C = m \\cdot c',
    variables: ['C_term', 'm', 'c_esp'],
    description: 'Quantidade de calor necessária para variar em 1 grau a temperatura de todo o corpo.',
    rearrangedLatex: {
      C_term: 'C = m \\cdot c',
      m: 'm = \\frac{C}{c}',
      c_esp: 'c = \\frac{C}{m}',
    },
    descriptorCode: 'FIS_D22',
  },
  {
    id: 'calor_capacidade',
    name: 'Calor em Função da Capacidade Térmica',
    subject: 'FISICA',
    latex: 'Q = C \\cdot \\Delta T',
    variables: ['Q_cal', 'C_term', 'deltaT'],
    description: 'Calor expresso diretamente pela capacidade térmica do sistema.',
    rearrangedLatex: {
      Q_cal: 'Q = C \\cdot \\Delta T',
      C_term: 'C = \\frac{Q}{\\Delta T}',
      deltaT: '\\Delta T = \\frac{Q}{C}',
    },
    descriptorCode: 'FIS_D22',
  },
  {
    id: 'calorimetria_latente',
    name: 'Calor Latente de Mudança de Fase',
    subject: 'FISICA',
    latex: 'Q = m \\cdot L',
    variables: ['Q_cal', 'm', 'L_lat'],
    description: 'Calor trocado durante uma transição de fase física a temperatura constante.',
    rearrangedLatex: {
      Q_cal: 'Q = m \\cdot L',
      m: 'm = \\frac{Q}{L}',
      L_lat: 'L = \\frac{Q}{m}',
    },
    descriptorCode: 'FIS_D22',
  },

  // --- ELETRICIDADE ---
  {
    id: 'primeira_lei_ohm',
    name: '1ª Lei de Ohm',
    subject: 'FISICA',
    latex: 'U = R \\cdot i',
    variables: ['U', 'R_el', 'i'],
    description: 'Tensão elétrica proporcional à intensidade da corrente em resistor ôhmico.',
    rearrangedLatex: {
      U: 'U = R \\cdot i',
      R_el: 'R = \\frac{U}{i}',
      i: 'i = \\frac{U}{R}',
    },
    descriptorCode: 'FIS_D42',
  },
  {
    id: 'corrente_eletrica',
    name: 'Definição de Corrente Elétrica',
    subject: 'FISICA',
    latex: 'i = \\frac{q}{\\Delta t} \\iff q = i \\cdot \\Delta t',
    variables: ['i', 'q_carga', 'deltat'],
    description: 'Quantidade de carga elétrica que atravessa a seção transversal por unidade de tempo.',
    rearrangedLatex: {
      i: 'i = \\frac{q}{\\Delta t}',
      q_carga: 'q = i \\cdot \\Delta t',
      deltat: '\\Delta t = \\frac{q}{i}',
    },
    descriptorCode: 'FIS_D42',
  },
  {
    id: 'potencia_eletrica_ui',
    name: 'Potência Elétrica (Tensão e Corrente)',
    subject: 'FISICA',
    latex: 'P_{el} = U \\cdot i',
    variables: ['Pel', 'U', 'i'],
    description: 'Potência elétrica convertida ou consumida no circuito elétrico.',
    rearrangedLatex: {
      Pel: 'P_{el} = U \\cdot i',
      U: 'U = \\frac{P_{el}}{i}',
      i: 'i = \\frac{P_{el}}{U}',
    },
    descriptorCode: 'FIS_D42',
    alternateDescriptorCodes: ['FIS_D37'],
  },
  {
    id: 'potencia_eletrica_ri2',
    name: 'Efeito Joule (Potência dissipada)',
    subject: 'FISICA',
    latex: 'P_{el} = R \\cdot i^2',
    variables: ['Pel', 'R_el', 'i'],
    description: 'Potência dissipada na forma de calor por efeito Joule em resistor ôhmico.',
    rearrangedLatex: {
      Pel: 'P_{el} = R \\cdot i^2',
      R_el: 'R = \\frac{P_{el}}{i^2}',
      i: 'i = \\sqrt{\\frac{P_{el}}{R}}',
    },
    descriptorCode: 'FIS_D42',
  },
  {
    id: 'potencia_eletrica_u2r',
    name: 'Potência Elétrica (Tensão e Resistência)',
    subject: 'FISICA',
    latex: 'P_{el} = \\frac{U^2}{R}',
    variables: ['Pel', 'U', 'R_el'],
    description: 'Potência elétrica expressa diretamente em função da ddp e da resistência elétrica.',
    rearrangedLatex: {
      Pel: 'P_{el} = \\frac{U^2}{R}',
      U: 'U = \\sqrt{P_{el} \\cdot R}',
      R_el: 'R = \\frac{U^2}{P_{el}}',
    },
    descriptorCode: 'FIS_D42',
  },
  {
    id: 'energia_eletrica',
    name: 'Consumo de Energia Elétrica',
    subject: 'FISICA',
    latex: 'E_{el} = P_{el} \\cdot \\Delta t',
    variables: ['Eel', 'Pel', 'deltat'],
    description: 'Energia elétrica total consumida por potência elétrica ao longo do tempo.',
    rearrangedLatex: {
      Eel: 'E_{el} = P_{el} \\cdot \\Delta t',
      Pel: 'P_{el} = \\frac{E_{el}}{\\Delta t}',
      deltat: '\\Delta t = \\frac{E_{el}}{P_{el}}',
    },
    descriptorCode: 'FIS_D39',
  },
];
